import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import { getEmployeePayslips, getPayslipPDF, getEmployeePayslipByMonth } from "../../../../../services/workspace/payrollManagement.service";
import { getStaffList } from "../../../../../services/workspace/staff.service";
import { FiDownload, FiEye, FiFileText } from "react-icons/fi";
import toast from "react-hot-toast";
import moment from "moment";
import { getUserList } from "../../../../../services/workspace/user.service";
import { convertToLabelValue } from "../../../../../utils/common";

const items = [{ label: "My Payslips", path: "" }];

const PayslipViewer = () => {
  const { employeeId } = useParams();
  const { userInfo, role: userType } = useSelector((state) => state.user);
  const loggedInUser = userInfo?.user;
  const isStaff = userType === 2; // Check if user type is 2 (staff)
  
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [payslip, setPayslip] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPayslip, setLoadingPayslip] = useState(false);

  useEffect(() => {
    fetchEmployees();
    // Set default month to current month
    const currentMonth = moment().format("YYYY-MM");
    setSelectedMonth(currentMonth);
  }, []);

  // Auto-select logged-in employee for staff (user type 2)
  useEffect(() => {
    if (isStaff && loggedInUser && employees.length > 0) {
      // Only auto-select if not already selected
      if (!selectedEmployee) {
        // Find the logged-in user in the employees list
        // Try matching by uuid first, then by id, and also check the original data
        const loggedInEmployee = employees.find(
          (emp) => {
            // Check value field (from convertToLabelValue)
            const valueMatch = emp.value === loggedInUser.uuid || emp.value === loggedInUser.id;
            
            // Check uuid field (if present in employee object)
            const uuidMatch = emp.uuid === loggedInUser.uuid || emp.uuid === loggedInUser.id;
            
            // Check id field (if present in employee object)
            const idMatch = emp.id === loggedInUser.id || emp.id === loggedInUser.uuid;
            
            const match = valueMatch || uuidMatch || idMatch;
            
            if (match) {
            }
            return match;
          }
        );
        
        if (loggedInEmployee) {
          setSelectedEmployee(loggedInEmployee);
        } else {
          console.warn('[PayslipViewer] Could not find logged-in employee in list', {
            loggedInUserUuid: loggedInUser?.uuid,
            loggedInUserId: loggedInUser?.id,
            employeeValues: employees.slice(0, 3).map(e => ({ 
              value: e.value, 
              uuid: e.uuid, 
              id: e.id,
              label: e.label 
            }))
          });
        }
      }
    }
  }, [isStaff, loggedInUser, employees]);

  useEffect(() => {
    if (selectedEmployee && selectedMonth) {
      fetchPayslipByEmployeeAndMonth();
    } else {
      setPayslip(null);
    }
  }, [selectedEmployee, selectedMonth]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response     = await getUserList({ type: 2, additional_info: true });;
    
      const staffData = response?.user?.data || response?.data || response || [];
        
      // Use false (or omit) for needUser since user objects have 'id' not 'user_id'
      const formattedLeads = convertToLabelValue(staffData, false);
      setEmployees(formattedLeads);
    } catch (error) {
      console.error("Employees fetch error:", error);
      toast.error("Failed to fetch employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslipByEmployeeAndMonth = async () => {
    if (!selectedEmployee || !selectedMonth) return;
      
    try {
      setLoadingPayslip(true);
      const employeeId = typeof selectedEmployee === 'object' 
        ? selectedEmployee.uuid 
        : selectedEmployee;
      
      // Extract month and year from selectedMonth (format: YYYY-MM)
      const month = moment(selectedMonth).format('MM'); // 01, 02, etc.
      const year = moment(selectedMonth).format('YYYY'); // 2026, etc.
      
      // Fetch payslip using the new API endpoint: /api/payrolls/employees/{employeeId}/payslip/generate?month=01&year=2026
      const response = await getEmployeePayslipByMonth(employeeId, month, year);
      
      // Handle response - API returns: { message, data: { download_url, payslip_id, file_name, period } }
      const payslipData = response?.data || response;
      
      if (payslipData && (payslipData.payslip_id || payslipData.download_url)) {
        // If payslip exists, set it with all the data from API
        setPayslip({
          id: payslipData.payslip_id,
          payslip_id: payslipData.payslip_id,
          download_url: payslipData.download_url,
          file_name: payslipData.file_name,
          period: payslipData.period,
          // Map other fields if available
          gross_salary: payslipData.gross_salary || payslipData.gross_pay || 0,
          net_pay: payslipData.net_pay || 0,
          status: payslipData.status || "PAID",
        });
      } else {
        // No payslip found
        setPayslip(null);
      }
    } catch (error) {
      console.error("Payslip fetch error:", error);
      // If 404 or no payslip found, set to null (don't show error toast)
      if (error?.response?.status === 404) {
        setPayslip(null);
      } else {
        toast.error(error?.response?.data?.message || "Failed to fetch payslip");
        setPayslip(null);
      }
    } finally {
      setLoadingPayslip(false);
    }
  };

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = moment().subtract(i, 'months');
      const value = date.format('YYYY-MM');
      const label = date.format('MMMM YYYY');
      months.push({ value, label });
    }
    return months;
  };

  const handleViewPayslip = async (payslip) => {
    try {
      setSelectedPayslip(payslip);
      toast.loading("Loading payslip PDF...");
      
      // If download_url is available, use it directly
      if (payslip?.download_url) {
        setPdfUrl(payslip.download_url);
        setShowPDFModal(true);
        toast.dismiss();
      } else {
        // Fallback to API call if download_url not available
        const blob = await getPayslipPDF(payslip.id || payslip.payslip_id);
      
      if (blob) {
        // Create object URL from blob
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPDFModal(true);
        toast.dismiss();
        }
      }
    } catch (error) {
      console.error("Payslip PDF error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to load payslip PDF");
    }
  };

  const handleDownloadPayslip = (payslip) => {
    try {
      // Use download_url directly from API response
      if (payslip?.download_url) {
        const link = document.createElement("a");
        link.href = payslip.download_url;
        link.download = payslip.file_name || `payslip_${payslip.payslip_id || payslip.id}.pdf`;
        link.target = "_blank"; // Open in new tab as fallback
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Payslip download started!");
      } else {
        // Fallback to API call if download_url not available
        handleDownloadPayslipViaAPI(payslip.id || payslip.payslip_id);
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download payslip");
    }
  };

  const handleDownloadPayslipViaAPI = async (payslipId) => {
    try {
      toast.loading("Downloading payslip...");
      
      // Fetch PDF blob
      const blob = await getPayslipPDF(payslipId);
      
      if (blob) {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `payslip_${payslipId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.dismiss();
        toast.success("Payslip downloaded successfully!");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to download payslip");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        isSearch={false}
        filter={false}
        shorting={false}
        breadcrumbs={items}
      />

      {/* Employee and Month Selection */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">View Payslip</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DefaultSearchSelectInput
            label="Select Employee"
            placeholder="Select employee"
            options={employees}
            value={selectedEmployee}
            onChange={(selected) => {
              if (!isStaff) {
                setSelectedEmployee(selected);
              }
            }}
            disabled={isStaff} // Disable for staff (user type 2)
            isDisabled={isStaff} // Also use isDisabled prop
          />
          <DefaultSearchSelectInput
            label="Select Month"
            placeholder="Select month"
            options={getMonthOptions()}
            value={getMonthOptions().find(m => m.value === selectedMonth) || null}
            onChange={(selected) => setSelectedMonth(selected?.value || "")}
          />
        </div>
      </div>

      {/* Payslip Display */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Payslip Details</h3>
        {loadingPayslip ? (
          <div className="text-center py-8 text-theme">Loading payslip...</div>
        ) : payslip ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-textLight">Period</p>
                <p className="font-semibold text-theme">{payslip.period || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-textLight">Payslip ID</p>
                <p className="font-semibold text-theme">{payslip.payslip_id || payslip.id || "N/A"}</p>
                </div>
              <div>
                <p className="text-sm text-textLight">File Name</p>
                <p className="font-semibold text-theme text-xs break-all">{payslip.file_name || "N/A"}</p>
                </div>
              <div>
                <p className="text-sm text-textLight">Status</p>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
                  Available
                </span>
              </div>
            </div>
            <div className="flex gap-2 pt-4 border-t border-borderColor">
              <DefaultButton
                name="View Payslip"
                icon={<FiEye className="w-4 h-4" />}
                onClick={() => handleViewPayslip(payslip)}
              />
              <DefaultButton
                name="Download"
                variant="outline"
                icon={<FiDownload className="w-4 h-4" />}
                onClick={() => handleDownloadPayslip(payslip)}
              />
            </div>
          </div>
        ) : selectedEmployee && selectedMonth ? (
          <div className="text-center py-8">
            <p className="text-textLight text-lg">No payslip found</p>
            <p className="text-sm text-textLight mt-2">
              Payroll run for this employee and month is not completed yet.
            </p>
      </div>
        ) : (
          <div className="text-center py-8 text-textLight">
            Please select employee and month to view payslip
          </div>
        )}
      </div>

      {/* PDF Modal */}
      {showPDFModal && selectedPayslip && (
        <CommonModal
          modalSize="4xl"
          headerTitle={`Payslip - ${selectedPayslip.period}`}
          onClose={() => {
            setShowPDFModal(false);
            setSelectedPayslip(null);
            if (pdfUrl) {
              window.URL.revokeObjectURL(pdfUrl);
            }
            setPdfUrl(null);
          }}
          show={showPDFModal}
        >
          <div className="flex flex-col gap-4">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-[600px] border border-borderColor rounded-lg"
                title="Payslip PDF"
              />
            ) : (
              <div className="text-center py-8 text-theme">Loading PDF...</div>
            )}
            <div className="flex justify-between items-center pt-4 border-t border-borderColor">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-primary-50 text-theme rounded text-xs font-medium">
                  QR: {selectedPayslip.qr_code}
                </span>
              </div>
              <div className="flex gap-2">
                <DefaultButton
                  name="Download"
                  icon={<FiDownload className="w-4 h-4" />}
                  onClick={() => handleDownloadPayslip(selectedPayslip)}
                />
                <DefaultButton
                  name="Print"
                  variant="outline"
                  onClick={() => window.print()}
                />
              </div>
            </div>
          </div>
        </CommonModal>
      )}
    </div>
  );
};

export default PayslipViewer;

